/**
 * useEntityList — fetches a Phase 3 per-entity REST endpoint
 * (`GET /api/agents`, `GET /api/opportunities`, etc.) and exposes the
 * resulting list with loading + error state and a manual refetch.
 *
 * Replaces the pattern of reading entity slices off the monolith
 * `tenant_bundles.bundle.*` via `useTenantData()`. Pages that have
 * been migrated stay independent of the bundle: they pull their
 * own data, react to their own loading state, and survive bundle
 * shape changes without ripples.
 *
 * Implementation notes
 *  - The state-update reducer is wrapped in `Promise.resolve().then(...)`
 *    so React 19's `react-hooks/set-state-in-effect` rule treats the
 *    update as a deferred microtask (the same pattern Wave 19 used in
 *    ApiTenantContext/ApiAuthContext).
 *  - `inFlight` ref tracks the latest request sequence so a late
 *    response from a stale path can't clobber newer state.
 *  - Each consumer fetches independently — these lists are small and
 *    a shared cache would re-introduce the staleness bugs the bundle
 *    overlay was invented to paper over.
 *  - `lastSyncedAt` is the wall-clock Date the last successful fetch
 *    resolved at. Consumers render this beside the count to turn the
 *    silent refetch into a visible confidence signal.
 */
import { useCallback, useEffect, useReducer, useRef } from "react";
import { api, ApiError } from "@/lib/api";

const LIST_INITIAL = { items: [], total: 0, loading: true, error: null, lastSyncedAt: null };

function listReducer(state, action) {
  switch (action.kind) {
    case "start":
      return { ...state, loading: true, error: null };
    case "success":
      return {
        items: action.items,
        total: action.total,
        loading: false,
        error: null,
        lastSyncedAt: action.lastSyncedAt,
      };
    case "failure":
      return { ...state, items: [], total: 0, loading: false, error: action.error };
    default:
      return state;
  }
}

export function useEntityList(path) {
  const [state, dispatch] = useReducer(listReducer, LIST_INITIAL);
  const inFlight = useRef(0);

  const fetchOnce = useCallback(() => {
    const seq = ++inFlight.current;
    Promise.resolve()
      .then(() => { if (seq === inFlight.current) dispatch({ kind: "start" }); })
      .then(() => api.get(path))
      .then((data) => {
        if (seq !== inFlight.current) return;
        dispatch({
          kind: "success",
          items: data?.items || [],
          total: data?.total ?? (data?.items?.length || 0),
          lastSyncedAt: new Date(),
        });
      })
      .catch((e) => {
        if (seq !== inFlight.current) return;
        dispatch({
          kind: "failure",
          error: e instanceof ApiError ? e : new ApiError(0, String(e)),
        });
      });
  }, [path]);

  useEffect(() => {
    fetchOnce();
  }, [fetchOnce]);

  return {
    items: state.items,
    total: state.total,
    loading: state.loading,
    error: state.error,
    lastSyncedAt: state.lastSyncedAt,
    refetch: fetchOnce,
  };
}

// ─────────────────────────────────────────────────────────────────────────
// useEntity — single-record sibling of useEntityList for detail pages.
// ─────────────────────────────────────────────────────────────────────────
const ONE_INITIAL = { item: null, loading: true, error: null, lastSyncedAt: null };

function oneReducer(state, action) {
  switch (action.kind) {
    case "start":
      return { ...state, loading: true, error: null };
    case "success":
      return { item: action.item, loading: false, error: null, lastSyncedAt: action.lastSyncedAt };
    case "failure":
      return { ...state, item: null, loading: false, error: action.error };
    default:
      return state;
  }
}

export function useEntity(path) {
  const [state, dispatch] = useReducer(oneReducer, ONE_INITIAL);
  const inFlight = useRef(0);

  const fetchOnce = useCallback(() => {
    const seq = ++inFlight.current;
    Promise.resolve()
      .then(() => { if (seq === inFlight.current) dispatch({ kind: "start" }); })
      .then(() => api.get(path))
      .then((item) => {
        if (seq !== inFlight.current) return;
        dispatch({ kind: "success", item, lastSyncedAt: new Date() });
      })
      .catch((e) => {
        if (seq !== inFlight.current) return;
        dispatch({
          kind: "failure",
          error: e instanceof ApiError ? e : new ApiError(0, String(e)),
        });
      });
  }, [path]);

  useEffect(() => {
    fetchOnce();
  }, [fetchOnce]);

  // `data` is exposed as an alias for `item` so single-record consumers
  // can keep the destructuring shape they use with `useEntityList`
  // (which exposes `items`). Both names work; pages may pick either.
  return {
    item: state.item,
    data: state.item,
    loading: state.loading,
    error: state.error,
    lastSyncedAt: state.lastSyncedAt,
    refetch: fetchOnce,
  };
}
